// <RelationshipTabs> — URL-addressable tab bar per DESIGN.update.md §7.4.
//
// Tab state is synced to a URL search param (default "tab") so operators
// can deep-link into a specific relationship view.

import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import "./RelationshipTabs.css";

export interface RelationshipTab {
  id: string;
  label: string;
  count?: number;
  render: () => ReactNode;
}

export interface RelationshipTabsProps {
  tabs: RelationshipTab[];
  defaultTabId: string;
  urlParam?: string;
}

export function RelationshipTabs({
  tabs,
  defaultTabId,
  urlParam = "tab",
}: RelationshipTabsProps): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTabId = useMemo(() => {
    const fromUrl = searchParams.get(urlParam);
    if (fromUrl && tabs.some((t) => t.id === fromUrl)) return fromUrl;
    return defaultTabId;
  }, [searchParams, urlParam, tabs, defaultTabId]);

  const handleTabClick = useCallback(
    (tabId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tabId === defaultTabId) {
            next.delete(urlParam);
          } else {
            next.set(urlParam, tabId);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, urlParam, defaultTabId],
  );

  const activeTab = tabs.find((t) => t.id === activeTabId);

  return (
    <div className="rb-rel-tabs">
      <div className="rb-rel-tabs__bar" role="tablist" aria-label="Detail tabs">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              className={
                isActive
                  ? "rb-rel-tabs__tab rb-rel-tabs__tab--active"
                  : "rb-rel-tabs__tab"
              }
              onClick={() => handleTabClick(tab.id)}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="rb-rel-tabs__badge">{tab.count}</span>
              )}
            </button>
          );
        })}
      </div>
      <div
        id={`tabpanel-${activeTabId}`}
        role="tabpanel"
        aria-labelledby={activeTabId}
        className="rb-rel-tabs__panel"
      >
        {activeTab?.render()}
      </div>
    </div>
  );
}
